/**
 * Working-day arithmetic in the Asia/Amman calendar (BUILD_SPEC.md §12,
 * §9.3).
 *
 * Shared between the scraper (completion / archive eligibility) and the
 * API archive cron (Milestone 10). Centralizing here keeps the weekend
 * definition and the day-counting algorithm in exactly one place.
 *
 * No external dependencies — Intl.DateTimeFormat handles the Asia/Amman
 * conversions so this module can be included by any app without npm
 * resolution issues. Asia/Amman is UTC+3 with no DST (Jordan abolished
 * DST in 2012) so "midnight Asia/Amman" ≡ UTC-3h = 21:00 UTC prev day.
 *
 * Spec definitions:
 *   • Weekend in Jordan = Friday + Saturday (configurable via the
 *     `weekend_days` setting, but this module accepts the list as a
 *     parameter so it doesn't reach into Prisma).
 *   • "N working days after D" means: start counting from the day
 *     AFTER D; skip weekend days; the result is the calendar date of
 *     the Nth counted day. The transaction's archive eligibility kicks
 *     in at MIDNIGHT Asia/Amman of the day AFTER that.
 */

const TIMEZONE = "Asia/Amman";
// Asia/Amman is UTC+3 with no DST; midnight Amman = 21:00 UTC prev day.
const AMM_OFFSET_H = 3;

export type Weekday =
  | "Sunday"
  | "Monday"
  | "Tuesday"
  | "Wednesday"
  | "Thursday"
  | "Friday"
  | "Saturday";

/** §12 default weekend per Jordan business calendar. */
export const DEFAULT_WEEKEND_DAYS: readonly Weekday[] = ["Friday", "Saturday"];

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Extracts the Asia/Amman calendar date parts from a UTC Date. */
function ammanParts(date: Date): { year: number; month0: number; day: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const map = new Map(parts.map((p) => [p.type, p.value]));
  return {
    year: parseInt(map.get("year") ?? "2000", 10),
    month0: parseInt(map.get("month") ?? "1", 10) - 1,
    day: parseInt(map.get("day") ?? "1", 10),
  };
}

/** Returns the Asia/Amman day-of-week name for a UTC Date. */
function ammanWeekday(date: Date): Weekday {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: TIMEZONE,
    weekday: "long",
  }).format(date) as Weekday;
}

/**
 * Returns the UTC timestamp for 00:00:00 Asia/Amman on the given
 * calendar date. Amman is UTC+3, so midnight Amman = 21:00 UTC prev day.
 * Date.UTC handles negative hours and day/month overflows correctly.
 */
function ammanMidnightUtc(year: number, month0: number, day: number): Date {
  return new Date(Date.UTC(year, month0, day, -AMM_OFFSET_H, 0, 0, 0));
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * True iff `date` (interpreted as Asia/Amman wall-clock) falls on a
 * weekend day. Operators can override via the `weekend_days` setting;
 * callers pass that array in.
 */
export function isWeekend(date: Date, weekend: readonly Weekday[] = DEFAULT_WEEKEND_DAYS): boolean {
  return weekend.includes(ammanWeekday(date));
}

/**
 * Adds `n` working days to `from` and returns the UTC instant for
 * 00:00:00 Asia/Amman of the day AFTER the Nth counted day. That
 * instant is when archive eligibility begins (§12 cron fires at 00:05).
 *
 * "Add n working days" means: starting from the day AFTER `from`,
 * count n days, skipping weekend days. Per §12.3:
 *   completedAt Wed → Thu(1), Sun(2), Mon(3) → archives Tue 00:00 AMM.
 *   completedAt Fri → Sun(1), Mon(2), Tue(3) → archives Wed 00:00 AMM.
 */
export function addWorkingDays(
  from: Date,
  n: number,
  weekend: readonly Weekday[] = DEFAULT_WEEKEND_DAYS,
): Date {
  if (!Number.isInteger(n) || n < 0) {
    throw new Error(`addWorkingDays: n must be a non-negative integer, got ${n}`);
  }

  // Start from the day AFTER `from` in Asia/Amman calendar.
  const { year, month0, day } = ammanParts(from);
  // cursor = UTC instant for midnight Amman on (from + 1 day).
  // Date.UTC handles month/day overflow.
  let cursor = ammanMidnightUtc(year, month0, day + 1);

  let counted = 0;
  while (counted < n) {
    if (!weekend.includes(ammanWeekday(cursor))) {
      counted += 1;
    }
    if (counted < n) {
      // Advance one calendar day in Asia/Amman. Adding 24h is safe here
      // because Amman has no DST; each calendar day is exactly 86400s.
      cursor = new Date(cursor.getTime() + 24 * 60 * 60_000);
    }
  }

  // Return midnight Amman of the day AFTER the Nth counted day.
  const { year: cy, month0: cm0, day: cd } = ammanParts(cursor);
  return ammanMidnightUtc(cy, cm0, cd + 1);
}
